// AlarmTab.jsx - 报警管理Tab组件
import React from 'react';
import { Table, Row, Col, Tag, Button, Space, Select } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const COLORS = { primary: '#00d4ff', text: '#ffffff', muted: 'rgba(255,255,255,0.6)', solar: '#f4c542', storage: '#52e8b0', load: '#ff5a7f', grid: '#3a8dde' };
const cardStyle = { background: 'transparent', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' };

const fmtTime = (t) => t ? new Date(t).toLocaleString() : '-';

const severityColor = { 1: 'green', 2: 'orange', 3: 'red', 4: 'purple' };
const severityText = { 1: '提示', 2: '警告', 3: '故障', 4: '严重' };

const alarmStatusColor = { 0: '#faad14', 1: '#52c41a', 2: '#999' };
const alarmStatusText = { 0: '待处理', 1: '已处理', 2: '已忽略' };
const emsStatusColor = { active: '#ff4d4f', cleared: '#52c41a' };
const emsStatusText = { active: '激活', cleared: '已恢复' };

const AlarmTab = ({ alarmStats, alarms, alarmsWithDef, alarmPageSize, fetchAlarms, handleAlarmStatusChange }) => {
  const { t } = useTranslation();

  return (
    <div style={{ padding: '16px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('cloud.total'), value: alarmStats.total, color: COLORS.primary },
          { label: t('cloud.todayNew'), value: alarmStats.today_count, color: COLORS.solar },
          { label: t('status.pending'), value: alarmStats.pending, color: '#faad14' },
          { label: t('status.processed'), value: alarmStats.processed, color: '#52c41a' },
        ].map(item => (
          <Col xs={12} sm={8} md={6} key={item.label}>
            <div style={{ ...cardStyle, padding: '12px' }}>
              <div style={{ color: COLORS.muted, fontSize: 11, marginBottom: 4 }}>{item.label}</div>
              <div style={{ color: item.color, fontSize: 18, fontWeight: 'bold' }}>{item.value}</div>
            </div>
          </Col>
        ))}
      </Row>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: COLORS.muted, fontSize: 12 }}>
          <Space size={12} align="center">
            <span>{t('cloud.pageShow')}:</span>
            <Select
              value={alarmPageSize}
              onChange={(v) => { /* setAlarmPageSize(v); fetchAlarms(1, true); */ }}
              style={{ width: 100 }}
              size="small"
              options={[
                { value: 10, label: `10 ${t('cloud.items')}` },
                { value: 20, label: `20 ${t('cloud.items')}` },
                { value: 50, label: `50 ${t('cloud.items')}` },
                { value: 100, label: `100 ${t('cloud.items')}` },
              ]}
            />
            <span style={{ marginLeft: 8 }}>{t('cloud.alarmRecords')} - {t('cloud.total')}{alarmStats.total}{t('cloud.records')}</span>
          </Space>
        </div>
        <Button icon={<SyncOutlined />} onClick={() => fetchAlarms()}>{t('common.refresh')}</Button>
      </div>

      <Table
        columns={[
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmCode')}</span>, dataIndex: 'alarm_code', key: 'alarm_code', render: t => <span style={{ color: COLORS.load }}>{t}</span>, width: 110 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmName')}</span>, dataIndex: 'name', key: 'name', render: t => t || '-', width: 160 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmReason')}</span>, dataIndex: 'reason_code', key: 'reason_code', render: t => t || '-' },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceType')}</span>, dataIndex: 'device_type', key: 'device_type', render: t => t || '-', width: 100 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.category')}</span>, dataIndex: 'category', key: 'category', render: t => t || '-', width: 100 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.level')}</span>, dataIndex: 'level', key: 'level', render: t => t || '-', width: 80 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.severityLevel')}</span>, dataIndex: 'severity', key: 'severity', render: v => <Tag color={severityColor[v]}>{severityText[v] || v}</Tag>, width: 80 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'entity_instance', key: 'entity_instance', width: 110 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.subDeviceId')}</span>, dataIndex: 'device_id', key: 'device_id', render: t => t || '-', width: 100 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceName')}</span>, dataIndex: 'sub_device_name', key: 'sub_device_name', render: t => t || '-', width: 120 },
          { title: 'sn', dataIndex: 'sn', key: 'sn', render: t => <span style={{ color: COLORS.primary }}>{t || '-'}</span>, width: 130 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.stationName')}</span>, dataIndex: 'station_name', key: 'station_name', render: t => t || '-', width: 130 },
          {
            title: <span style={{whiteSpace:'nowrap'}}>EMS状态</span>,
            dataIndex: 'ems_status',
            key: 'ems_status',
            render: v => <Tag color={emsStatusColor[v]}>{emsStatusText[v] || v || '-'}</Tag>,
            width: 90,
          },
          {
            title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.status')}</span>,
            dataIndex: 'process_status',
            key: 'process_status',
            render: (v, record) => (
              <Select
                value={v}
                onChange={(status) => handleAlarmStatusChange(record.id, status)}
                style={{ width: 100 }}
                size="small"
                options={[
                  { value: 0, label: t('status.pending') },
                  { value: 1, label: t('status.processed') },
                  { value: 2, label: t('status.ignored') },
                ]}
              />
            )
          },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.handler')}</span>, dataIndex: 'handler_name', key: 'handler_name', render: t => t || '-' },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.handlerNote')}</span>, dataIndex: 'handler_note', key: 'handler_note', render: t => t || '-', width: 170 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.handleTime')}</span>, dataIndex: 'handled_at', key: 'handled_at', render: fmtTime },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.updatedTime')}</span>, dataIndex: 'updated_at', key: 'updated_at', render: fmtTime },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.occurTime')}</span>, dataIndex: 'raise_time', key: 'raise_time', render: fmtTime },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.occurredAt')}</span>, dataIndex: 'occurred_at', key: 'occurred_at', render: fmtTime },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.clearedAt')}</span>, dataIndex: 'cleared_at', key: 'cleared_at', render: fmtTime },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.description')}</span>, dataIndex: 'description', key: 'description', render: t => t || '-', width: 200 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.suggestion')}</span>, dataIndex: 'suggestion', key: 'suggestion', render: t => t || '-', width: 200 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.additionalParams')}</span>, dataIndex: 'additional_param', key: 'additional_param', render: t => typeof t === 'string' ? t : (t ? JSON.stringify(t) : '-'), width: 150 },
          { title: <span style={{whiteSpace:'nowrap'}}>message_id</span>, dataIndex: 'message_id', key: 'message_id', render: t => t || '-', width: 150 },
          { title: <span style={{whiteSpace:'nowrap'}}>alarm_id</span>, dataIndex: 'alarm_id', key: 'alarm_id', render: t => t || '-', width: 120 },
          { title: <span style={{whiteSpace:'nowrap'}}>is_done</span>, dataIndex: 'is_done', key: 'is_done', render: v => v == null ? '-' : (v ? '是' : '否'), width: 70 },
          { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.createdAt')}</span>, dataIndex: 'created_at', key: 'created_at', render: fmtTime },
        ]}
        dataSource={alarms}
        rowKey="id"
        size="small"
        pagination={{ pageSize: alarmPageSize, showSizeChanger: false }}
        style={{ background: 'transparent' }}
        scroll={{ x: 3200 }}
        tableLayout="fixed" />

      {/* 告警记录+定义关联列表 */}
      <div style={{ marginTop: 24 }}>
        <div style={{ color: COLORS.primary, fontSize: 14, fontWeight: 600, marginBottom: 8, borderLeft: '3px solid #ff5a7f', paddingLeft: 8 }}>
          {t('cloud.alarmRecordDef')} - {alarmsWithDef.length}{t('cloud.records')}
        </div>
        <Table
          columns={[
            { title: <span style={{whiteSpace:'nowrap'}}>定义ID</span>, dataIndex: 'def_id', key: 'def_id', width: 70, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmCode')}</span>, dataIndex: 'alarm_code', key: 'alarm_code', width: 110, render: v => <span style={{ color: COLORS.load }}>{v}</span> },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceType')}</span>, dataIndex: 'def_type', key: 'def_type', width: 100, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.standardAlarmName')}</span>, dataIndex: 'standard_alarm_name', key: 'standard_alarm_name', width: 200, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmLevel')}</span>, dataIndex: 'def_severity', key: 'def_severity', width: 90, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.levelMeaning')}</span>, dataIndex: 'level_meaning', key: 'level_meaning', width: 100, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.lifecycle')}</span>, dataIndex: 'lifecycle', key: 'lifecycle', width: 90, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.mergeStrategy')}</span>, dataIndex: 'merge_strategy', key: 'merge_strategy', width: 100, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.alarmTypeDefinition')}</span>, dataIndex: 'alarm_type_definition', key: 'alarm_type_definition', width: 130, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.synonymName')}</span>, dataIndex: 'synonym_name', key: 'synonym_name', width: 130, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.referenceTag')}</span>, dataIndex: 'reference_tag', key: 'reference_tag', width: 130, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.sourcePointCount')}</span>, dataIndex: 'source_point_count', key: 'source_point_count', width: 120, render: v => v ?? '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.sourceTemplateCount')}</span>, dataIndex: 'source_template_count', key: 'source_template_count', width: 130, render: v => v ?? '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.maturity')}</span>, dataIndex: 'maturity', key: 'maturity', width: 80, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.remark')}</span>, dataIndex: 'remark', key: 'remark', width: 150, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceNo')}</span>, dataIndex: 'entity_instance', key: 'entity_instance', width: 120, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.deviceName')}</span>, dataIndex: 'sub_device_name', key: 'sub_device_name', width: 120, render: v => v || '-' },
            { title: 'sn', dataIndex: 'sn', key: 'sn', width: 100, render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.stationName')}</span>, dataIndex: 'station_name', key: 'station_name', width: 130, render: v => v || '-' },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.recordLevel')}</span>, dataIndex: 'record_severity', key: 'record_severity', width: 80, render: v => <Tag color={severityColor[v]}>{severityText[v] || v}</Tag> },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.status')}</span>, dataIndex: 'status', key: 'status', width: 80, render: v => <Tag color={alarmStatusColor[v]}>{alarmStatusText[v]}</Tag> },
            { title: <span style={{whiteSpace:'nowrap'}}>{t('cloud.occurTime')}</span>, dataIndex: 'raise_time', key: 'raise_time', width: 160, render: fmtTime },
          ]}
          dataSource={alarmsWithDef}
          rowKey="record_id"
          size="small"
          pagination={{ pageSize: alarmPageSize, showSizeChanger: false }}
          style={{ background: 'transparent' }}
          scroll={{ x: 3600 }}
          tableLayout="fixed" />
      </div>
    </div>
  );
};

export default AlarmTab;
